import React, { useEffect, useCallback, useState, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { css } from "@emotion/css";
import { MathMarkdown, MathText } from "../../components/MathMarkdown";
import { useArcGenerationRequest } from "../../hooks/useArcGenerationRequest";
import {
  useArcGenerationSessionStore,
  useArcGenState,
  useArcGenIsStreaming,
  useArcGenCompletedLists,
  useArcGenError,
} from "../../stores/arcGenerationSessionStore";
import type {
  ArcInteractionSpec,
  ArcDomainRecommendationSpec,
  ArcQuestRecommendationSpec,
  ArcMultiSelectSpec,
} from "../../types/arcGenerationSession";
import { useDomainCatalog } from "../../data/domainCatalog";
import { DomainStrip } from "./DomainStrip";
import { QuestRecommendation } from "./QuestRecommendation";
import { ExploreQuests } from "./ExploreQuests";
import { QuestEscapeHatch } from "./QuestEscapeHatch";
import { OnboardingProgress } from "./OnboardingProgress";
import { ResonanceArcSession } from "./ResonanceArcSession";
import { PendingIndicator } from "../../components/PendingIndicator";

// ── Styles ──────────────────────────────────────────────────────────

const pageStyle = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 720px;
  margin: 0 auto;
  background: var(--color-bg);
  color: var(--color-text);
  padding: 16px;
`;

const headerRowStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const titleAreaStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const headerStyle = css`
  font-size: 1.3em;
  font-weight: 700;
  color: var(--color-text);
`;

const backButtonStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: var(--color-bg-elevated);
  color: var(--color-text-secondary);
  font-size: 1.1em;
  cursor: pointer;
  flex-shrink: 0;
  transition: border-color 0.15s, background 0.15s;

  &:hover:not(:disabled) {
    border-color: var(--color-primary);
    color: var(--color-text);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// ─── Step-progress bar row (replaces the corner pill) ────────────
//
// One thin rectangle per question in the current step, colored by
// status: completed / current / upcoming.  Sits directly under the
// header so the learner sees "where am I in this step" at a glance
// without scanning the corner of the screen.  The "Question N of M"
// label is right-aligned next to the bars — paired with what it
// describes instead of floating in the header.
//
// Visual reference: a minimal progress strip à la Linear / Notion.
// Heights are kept small so the strip doesn't compete with the
// header for visual weight.
const progressRowStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: -4px 0 18px;        /* tucks under the header */
  padding: 0 4px;
`;

const progressBarsStyle = css`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const progressBarStyle = css`
  flex: 1 1 0;
  height: 4px;
  border-radius: 999px;
  background: var(--color-border-subtle);
  transition: background 0.2s ease, transform 0.2s ease;
`;

// Completed question — solid accent.
const progressBarDoneStyle = css`
  background: var(--color-primary);
`;

// Current question — accent at lighter weight + a hairline ring so
// it reads as "here, in progress" without competing with the
// completed-color saturation.  Slightly taller via transform so the
// active step has a small physical lift.
const progressBarActiveStyle = css`
  background: var(--color-primary);
  opacity: 0.45;
  transform: scaleY(1.5);
`;

const progressLabelStyle = css`
  font-size: 0.78em;
  font-weight: 600;
  color: var(--color-text-muted);
  letter-spacing: 0.02em;
  white-space: nowrap;
  flex-shrink: 0;
`;

// Recipe-aware fallback counts when the session state hasn't yet
// shipped its ``recipeMeta`` block (e.g. an old session loaded from
// the DB before the field was added).  The active recipe's counts
// arrive on the very first state_snapshot frame, so this fallback is
// only used for the initial pre-snapshot render.
const FALLBACK_PSI_QUESTION_COUNT = 3;
const FALLBACK_THETA_QUESTION_COUNT = 3;

const turnsAreaStyle = css`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 16px;
`;

const turnStyle = css`
  margin-bottom: 12px;
`;

const coachBubbleStyle = css`
  background: var(--color-coach-bubble-bg);
  color: var(--color-coach-bubble-text);
  border-radius: 12px;
  padding: 12px 16px;
  max-width: 98%;
  font-size: 0.95em;
  line-height: 1.5;

  /* Honor multi-paragraph markdown — without these the bubble
     collapses paragraphs into a single block of text, which is
     exactly the wall-of-text problem the quest-intro feedback
     called out. */
  p {
    margin: 0 0 12px 0;
  }
  p:last-child {
    margin-bottom: 0;
  }
  ul, ol {
    margin: 0 0 12px 0;
    padding-left: 22px;
  }
  ul:last-child, ol:last-child {
    margin-bottom: 0;
  }
  li {
    margin-bottom: 4px;
  }
  strong, b {
    color: var(--color-text);
  }
`;

const questIntroBubbleStyle = css`
  /* Wider than coach bubbles for narrative content; otherwise same
     markdown spacing rules as coachBubbleStyle. */
  background: var(--color-coach-bubble-bg);
  color: var(--color-coach-bubble-text);
  border-radius: 12px;
  padding: 14px 18px;
  font-size: 0.95em;
  line-height: 1.55;
  margin-bottom: 16px;

  p {
    margin: 0 0 12px 0;
  }
  p:last-child {
    margin-bottom: 0;
  }
  ul, ol {
    margin: 0 0 12px 0;
    padding-left: 22px;
  }
  ul:last-child, ol:last-child {
    margin-bottom: 0;
  }
  li {
    margin-bottom: 4px;
  }
  strong, b {
    color: var(--color-text);
  }
`;

const inputAreaStyle = css`
  border-top: 1px solid var(--color-border-subtle);
  padding-top: 16px;
`;

const textareaStyle = css`
  width: 100%;
  min-height: 80px;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  padding: 12px;
  background: var(--color-bg-elevated);
  color: var(--color-text);
  font-family: inherit;
  font-size: 0.95em;
  line-height: 1.5;
  resize: vertical;
  box-sizing: border-box;

  &:focus { outline: none; border-color: var(--color-primary); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  &::placeholder { color: var(--color-text-muted); }
`;

const submitBtnStyle = css`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 10px;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
  transition: background 0.15s ease;

  &:hover:not(:disabled) { background: var(--color-primary-hover); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const sparkChipsWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

const sparkChipsLabelStyle = css`
  font-size: 0.78em;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const sparkChipsRowStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const sparkChipStyle = css`
  appearance: none;
  border: 1px solid var(--color-border);
  background: var(--color-bg-elevated);
  color: var(--color-text-secondary);
  padding: 8px 12px;
  border-radius: 999px;
  font-family: inherit;
  font-size: 0.85em;
  line-height: 1.35;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, transform 0.05s ease;

  &:hover:not(:disabled) {
    border-color: var(--color-primary);
    color: var(--color-text);
    background: var(--color-bg);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Q1_SPARK_CHIPS: readonly string[] = [
  "I want to understand modern AI from first principles and strengthen my intuition",
  "I want to deepen what I already know, well past tutorials",
  "I want a daily learning habit that compounds over years",
  "I want to be top-tier at AI, not just credentialed",
  "I want to build something that requires real AI depth",
] as const;

const optionBtnStyle = css`
  display: block;
  width: 100%;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-bg-elevated);
  color: var(--color-text);
  text-align: left;
  font-size: 0.95em;
  cursor: pointer;
  margin-bottom: 8px;
  transition: border-color 0.15s, background 0.15s;

  &:hover:not(:disabled) { border-color: var(--color-primary); background: var(--color-primary-muted); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const multiOptionSelectedStyle = css`
  border-color: var(--color-primary) !important;
  background: var(--color-primary-muted) !important;
  border-width: 2px !important;
  padding: 11px !important;
`;

const checkboxStyle = css`
  display: inline-block;
  margin-right: 10px;
  font-size: 1.1em;
  color: var(--color-primary);
  vertical-align: middle;
`;

const multiSelectHintStyle = css`
  font-size: 0.82em;
  color: var(--color-text-muted);
  margin-bottom: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

/* Calibration feedback bubble — distinct from coachBubbleStyle so
 * the "this is feedback on your last answer" framing reads as a
 * different beat from the next question. */
const calibrationFeedbackStyle = css`
  background: var(--color-bg-elevated);
  color: var(--color-text);
  border-radius: 12px;
  padding: 14px 18px;
  font-size: 0.95em;
  line-height: 1.55;
  margin-bottom: 14px;
  border-left: 3px solid var(--color-primary);

  p { margin: 0 0 8px 0; }
  p:last-child { margin-bottom: 0; }
  strong, b { color: var(--color-text); }
`;

const calibrationContinueBtnStyle = css`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 10px;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  margin-top: 4px;
  transition: background 0.15s ease;

  &:hover { background: var(--color-primary-hover); }
`;

const otherInputAreaStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid var(--color-primary);
  border-radius: 10px;
  background: var(--color-primary-muted);
`;

const otherTextareaStyle = css`
  width: 100%;
  min-height: 48px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 10px;
  background: var(--color-bg-elevated);
  color: var(--color-text);
  font-family: inherit;
  font-size: 0.93em;
  line-height: 1.4;
  box-sizing: border-box;
  resize: none;

  &:focus { outline: none; border-color: var(--color-primary); }
  &::placeholder { color: var(--color-text-muted); }
`;

const loadingStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--color-text-muted);
`;

const errorStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--color-error);
  padding: 24px;
`;

const completedStyle = css`
  text-align: center;
  padding: 20px 0;
  color: var(--color-text-secondary);
`;

// ── Component ───────────────────────────────────────────────────────

export const ArcGenerationSession: React.FC = () => {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { sendArcGenAction } = useArcGenerationRequest();
  const sessionState = useArcGenState();
  const isStreaming = useArcGenIsStreaming();
  const completedLists = useArcGenCompletedLists();
  const error = useArcGenError();
  const reset = useArcGenerationSessionStore((s) => s.reset);
  const [textInput, setTextInput] = useState("");
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherText, setOtherText] = useState("");
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(null);
  const [exploreQuestsOpen, setExploreQuestsOpen] = useState(false);
  const { domains: domainsById } = useDomainCatalog();
  // Local "submitting" flag for the domain CTA — true between the
  // user clicking Continue and the first server frame arriving (which
  // changes the step away from domain_recommend and naturally hides
  // the CTA).  Without this, the CTA stays clickable during the
  // websocket roundtrip and feels like nothing is happening.
  const [submittingDomain, setSubmittingDomain] = useState(false);
  // Local "starting" flag for the quest CTA — true from the user
  // clicking "Start this quest" until the session completes.  The
  // hero can be started mid-stream (it cancels the rest of the map
  // generation), so this also drives a "Starting your quest…"
  // affirmation while the select_quest roundtrip lands.
  const [submittingQuest, setSubmittingQuest] = useState(false);
  // Local selection state for multi_select interactions.  Keyed by
  // interactionId so navigating-back-then-forward doesn't leak picks
  // from a different question.
  const [multiSelectPicks, setMultiSelectPicks] = useState<Record<string, string[]>>({});
  // Theta calibration feedback phase: when a theta question lands
  // with feedback on the *previous* answer in its explanation, we
  // show that feedback as a separate bubble with a Continue button
  // BEFORE revealing the new question.  Mirrors the coaching-session
  // pattern.  Keyed by interactionId so navigating-back-then-forward
  // doesn't leak the dismissed state across different questions.
  const [thetaFeedbackDismissed, setThetaFeedbackDismissed] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    reset();
    if (sessionId) {
      sendArcGenAction("arc_generation_get", { sessionId });
    } else {
      const reason = searchParams.get("reason") || "onboarding";
      // Optional recipe override from the URL (e.g. ?recipe=stretch_probe).
      // Backend falls back to the env-configured default when omitted.
      const recipe = searchParams.get("recipe");
      // Superuser-only: ?cloneFrom=<sessionId> tells the backend
      // to deep-copy an existing arc-gen session's state into a
      // brand-new session so the team can reproduce/replay from
      // any snapshot.  Backend ignores this for non-superusers.
      const cloneFrom = searchParams.get("cloneFrom");
      const payload: Record<string, string> = { reason };
      if (recipe) payload.recipeId = recipe;
      if (cloneFrom) payload.cloneFromSessionId = cloneFrom;
      sendArcGenAction("arc_generation_start", payload);
    }
  }, [sessionId]);

  const activeScope = sessionState ? sessionState.scopes[sessionState.activeScopeId] : null;
  const activity = activeScope?.work;
  const activeInteraction: ArcInteractionSpec | null = useMemo(() => {
    if (!activity?.activeInteractionId) return null;
    return activity.interactions[activity.activeInteractionId] ?? null;
  }, [activity?.activeInteractionId, activity?.interactions]);

  // Count free_text + single_select interactions stamped with the
  // current step (psi or theta_quiz).  Answered + currently-active
  // count; abandoned ones (e.g. after navigate_back) are excluded so
  // the label tracks "which question am I on right now".  Totals come
  // from the session's ``recipeMeta`` (recipe-aware) with a fallback
  // for the pre-snapshot render.  We track ``answered`` and
  // ``active`` separately so the progress bars can render an active
  // bar distinctly from the completed ones — ``count`` (= answered
  // + active) is kept for the existing showCounter check + the
  // "Question N of M" label.
  const currentStepQuestions = useMemo(() => {
    if (!activity) return { count: 0, answered: 0, active: 0, total: 0 };
    const step = sessionState?.step;
    const psiTotal =
      sessionState?.recipeMeta?.psiQuestionCount ?? FALLBACK_PSI_QUESTION_COUNT;
    const thetaTotal =
      sessionState?.recipeMeta?.thetaQuestionCount ??
      FALLBACK_THETA_QUESTION_COUNT;
    const total = step === "psi" ? psiTotal : step === "theta_quiz" ? thetaTotal : 0;
    let answered = 0;
    let active = 0;
    for (const id of activity.interactionOrder ?? []) {
      const it = activity.interactions[id];
      const isQuestion =
        it?.kind === "free_text" ||
        it?.kind === "single_select" ||
        it?.kind === "multi_select";
      if (isQuestion && it?.step === step) {
        if (it?.status === "answered") answered += 1;
        else if (it?.status === "active") active += 1;
      }
    }
    return { count: answered + active, answered, active, total };
  }, [
    activity?.interactionOrder,
    activity?.interactions,
    sessionState?.step,
    sessionState?.recipeMeta?.psiQuestionCount,
    sessionState?.recipeMeta?.thetaQuestionCount,
  ]);

  const showCounter =
    !!sessionState &&
    (sessionState.step === "psi" || sessionState.step === "theta_quiz") &&
    currentStepQuestions.count > 0;

  const submissionPending = Boolean(
    isStreaming || (activeInteraction as any)?.submission?.pending
  );

  // Back is available when there's at least one previously-answered PSI
  // interaction to revert to, and we're still in a phase where back makes
  // sense (psi or domain_recommend — once the arc is activated, back is
  // disallowed by the backend handler).
  const answeredCount = useMemo(() => {
    if (!activity) return 0;
    let count = 0;
    for (const id of activity.interactionOrder ?? []) {
      const it = activity.interactions[id];
      if (
        (it?.kind === "free_text" ||
          it?.kind === "single_select" ||
          it?.kind === "multi_select") &&
        it?.status === "answered"
      ) {
        count += 1;
      }
    }
    return count;
  }, [activity?.interactionOrder, activity?.interactions]);

  const canGoBack =
    !!sessionState &&
    !submissionPending &&
    (sessionState.step === "psi" ||
      sessionState.step === "domain_recommend" ||
      sessionState.step === "theta_quiz" ||
      sessionState.step === "quest_recommend") &&
    answeredCount > 0;

  // Latest domain_recommendation interaction
  const latestRecommendations = useMemo(() => {
    if (!activity) return [];
    const order = activity.interactionOrder ?? [];
    for (let i = order.length - 1; i >= 0; i--) {
      const it = activity.interactions[order[i]];
      if (it?.kind === "domain_recommendation" && it?.status !== "abandoned") {
        return (it as ArcDomainRecommendationSpec).recommendations ?? [];
      }
    }
    return [];
  }, [activity?.interactionOrder, activity?.interactions]);

  // Latest quest_recommendation interaction (skip abandoned ones — those
  // are the result of request_more_quests regenerating).
  const latestQuestRec = useMemo(() => {
    if (!activity) return null;
    const order = activity.interactionOrder ?? [];
    for (let i = order.length - 1; i >= 0; i--) {
      const it = activity.interactions[order[i]];
      if (it?.kind === "quest_recommendation" && it?.status !== "abandoned") {
        return it as ArcQuestRecommendationSpec;
      }
    }
    return null;
  }, [activity?.interactionOrder, activity?.interactions]);

  // The best_fit quest streams in its own ``heroQuest`` field
  // (before the alternatives); ``quests`` holds everything else.
  const latestHeroQuest = latestQuestRec?.heroQuest ?? [];
  const latestQuests = latestQuestRec?.quests ?? [];
  // The full set — hero + the rest — for the explore overlay / map.
  const allQuests = useMemo(
    () => [...latestHeroQuest, ...latestQuests],
    [latestHeroQuest, latestQuests],
  );
  const latestCategories = latestQuestRec?.categories ?? [];
  // Milestones stream in two lists — the best_fit quest's
  // (``heroMilestones``) first, then every other quest's
  // (``milestones``).  Downstream consumers (cards, map) want the
  // combined view, so merge them here.
  const latestMilestones = useMemo(
    () => [
      ...(latestQuestRec?.heroMilestones ?? []),
      ...(latestQuestRec?.milestones ?? []),
    ],
    [latestQuestRec?.heroMilestones, latestQuestRec?.milestones],
  );

  // The hero (best_fit) quest is "ready to start" once its own
  // milestones have fully streamed AND been checkpoint-saved on the
  // backend — signalled by the ``heroMilestones`` list_done frame —
  // or once the whole envelope has finished.  At that point a
  // learner can start the hero even though the rest of the map is
  // still generating.
  const heroReady =
    !isStreaming || completedLists.includes("heroMilestones");

  // Whether the active interaction has a prior answered theta_quiz
  // interaction in the same scope.  Used to decide if we should
  // show the calibration-feedback phase before revealing the next
  // theta question.
  const hasPriorAnsweredTheta = useMemo(() => {
    if (!activity || !activeInteraction) return false;
    if (activeInteraction.kind !== "single_select") return false;
    if ((activeInteraction as any).step !== "theta_quiz") return false;
    for (const id of activity.interactionOrder ?? []) {
      if (id === activeInteraction.interactionId) continue;
      const it = activity.interactions[id];
      if (
        it?.step === "theta_quiz" &&
        it?.kind === "single_select" &&
        it?.status === "answered"
      ) {
        return true;
      }
    }
    return false;
  }, [activity?.interactionOrder, activity?.interactions, activeInteraction]);

  // True when the calibration-feedback bubble should be the only
  // thing on the input area — question/options are HIDDEN during
  // this phase, the preamble carries the feedback on the prior
  // answer, and a Continue button bridges to the question phase.
  const showCalibrationFeedback =
    !!activeInteraction &&
    hasPriorAnsweredTheta &&
    !thetaFeedbackDismissed[activeInteraction.interactionId];

  // Sync selectedDomainId from state (e.g., on resume)
  useEffect(() => {
    if (sessionState?.selectedDomainId) {
      setSelectedDomainId(sessionState.selectedDomainId);
    }
  }, [sessionState?.selectedDomainId]);

  // Auto-select the best_start domain when recommendations stream in
  useEffect(() => {
    if (latestRecommendations.length > 0 && !selectedDomainId) {
      const bestStart = latestRecommendations.find(
        (r) => r.recommendationTier === "best_start"
      );
      if (bestStart?.domainId) setSelectedDomainId(bestStart.domainId);
    }
  }, [latestRecommendations, selectedDomainId]);

  const buildPayload = useCallback(
    (clientEvent: Record<string, unknown>) => ({
      context: { interaction: activeInteraction ?? undefined },
      client_event: clientEvent,
    }),
    [activeInteraction]
  );

  const setPendingSubmission = useArcGenerationSessionStore((s) => s.setPendingSubmission);

  const handleSubmitFreeText = useCallback(() => {
    if (!sessionState || isStreaming || !textInput.trim() || !activeInteraction) return;
    const interactionId = activeInteraction.interactionId;
    const text = textInput.trim();
    const eventId = crypto.randomUUID();
    setPendingSubmission(interactionId, { eventId, displayText: text });
    sendArcGenAction("arc_generation_dispatch", {
      sessionId: sessionState.sessionId,
      event: {
        eventId,
        sessionId: sessionState.sessionId,
        clientTimestamp: new Date().toISOString(),
        event: {
          type: "submit_response",
          payload: buildPayload({ interactionId, value: { kind: "free_text", text } }),
        },
      },
    });
    setTextInput("");
  }, [sessionState, isStreaming, textInput, activeInteraction, sendArcGenAction, buildPayload, setPendingSubmission]);

  const handleSelectOption = useCallback(
    (optionId: string, label: string) => {
      if (!sessionState || isStreaming || !activeInteraction) return;
      const interactionId = activeInteraction.interactionId;
      const eventId = crypto.randomUUID();
      setPendingSubmission(interactionId, { eventId, displayText: label });
      sendArcGenAction("arc_generation_dispatch", {
        sessionId: sessionState.sessionId,
        event: {
          eventId,
          sessionId: sessionState.sessionId,
          clientTimestamp: new Date().toISOString(),
          event: {
            type: "submit_response",
            payload: buildPayload({ interactionId, value: { kind: "single_select", optionId } }),
          },
        },
      });
    },
    [sessionState, isStreaming, activeInteraction, sendArcGenAction, buildPayload, setPendingSubmission]
  );

  const handleSubmitMultiSelect = useCallback(() => {
    if (!sessionState || isStreaming || !activeInteraction) return;
    if (activeInteraction.kind !== "multi_select") return;
    const interactionId = activeInteraction.interactionId;
    const optionIds = multiSelectPicks[interactionId] ?? [];
    if (optionIds.length === 0) return;
    const options = (activeInteraction as ArcMultiSelectSpec).prompt?.options ?? [];
    const labelById = Object.fromEntries(options.map((o) => [o.id, o.label]));
    const displayText = optionIds.map((id) => labelById[id] ?? id).join(", ");
    const eventId = crypto.randomUUID();
    setPendingSubmission(interactionId, { eventId, displayText });
    sendArcGenAction("arc_generation_dispatch", {
      sessionId: sessionState.sessionId,
      event: {
        eventId,
        sessionId: sessionState.sessionId,
        clientTimestamp: new Date().toISOString(),
        event: {
          type: "submit_response",
          payload: buildPayload({ interactionId, value: { kind: "multi_select", optionIds } }),
        },
      },
    });
    // Clear local picks for this interaction; if learner comes back
    // they'll re-pick.
    setMultiSelectPicks((prev) => {
      const next = { ...prev };
      delete next[interactionId];
      return next;
    });
  }, [sessionState, isStreaming, activeInteraction, multiSelectPicks, sendArcGenAction, buildPayload, setPendingSubmission]);

  const handleSelectDomain = useCallback(
    (domainId: string) => {
      setSelectedDomainId(domainId);
    },
    []
  );

  const handleConfirmDomain = useCallback(
    (override?: string) => {
      const domainId = override ?? selectedDomainId;
      if (!sessionState || isStreaming || !domainId || submittingDomain) return;
      // Reflect the choice locally so the UI doesn't flash a
      // different selection before the server echoes.
      if (override && override !== selectedDomainId) {
        setSelectedDomainId(override);
      }
      setSubmittingDomain(true);
      const eventId = crypto.randomUUID();
      sendArcGenAction("arc_generation_dispatch", {
        sessionId: sessionState.sessionId,
        event: {
          eventId,
          sessionId: sessionState.sessionId,
          clientTimestamp: new Date().toISOString(),
          event: {
            type: "select_domain",
            payload: buildPayload({ domainId }),
          },
        },
      });
    },
    [sessionState, isStreaming, selectedDomainId, submittingDomain, sendArcGenAction, buildPayload],
  );

  // Clear the local submitting flag once the server moves us off
  // domain_recommend (i.e. the first frame for theta_quiz arrived).
  useEffect(() => {
    if (sessionState?.step !== "domain_recommend") {
      setSubmittingDomain(false);
    }
  }, [sessionState?.step]);

  // Clear the quest-starting flag if select_quest fails — otherwise
  // the "Starting your quest…" CTA would stay stuck.  On success the
  // session completes and the screen redirects, so no reset needed
  // for the happy path.
  useEffect(() => {
    if (error) setSubmittingQuest(false);
  }, [error]);

  const handleNavigateBack = useCallback(() => {
    if (!sessionState || isStreaming) return;
    // Clear local domain selection — going back regenerates the prior turn.
    setSelectedDomainId(null);
    const eventId = crypto.randomUUID();
    sendArcGenAction("arc_generation_dispatch", {
      sessionId: sessionState.sessionId,
      event: {
        eventId,
        sessionId: sessionState.sessionId,
        clientTimestamp: new Date().toISOString(),
        event: {
          type: "navigate_back",
          payload: buildPayload({}),
        },
      },
    });
  }, [sessionState, isStreaming, sendArcGenAction, buildPayload]);

  const handleChooseQuest = useCallback(
    (questId: string) => {
      // NOTE: intentionally NOT gated on ``isStreaming``.  The hero
      // quest can be started while the rest of the map is still
      // streaming — ``sendArcGenAction`` cancels the in-flight
      // quest_recommendation request (closing its WS), and the
      // backend has already checkpoint-saved the hero quest + its
      // milestones, so select_quest finds them.  Callers gate their
      // own buttons (hero on ``heroReady``, map on ``!isStreaming``).
      if (!sessionState || submittingQuest) return;
      setSubmittingQuest(true);
      const eventId = crypto.randomUUID();
      sendArcGenAction("arc_generation_dispatch", {
        sessionId: sessionState.sessionId,
        event: {
          eventId,
          sessionId: sessionState.sessionId,
          clientTimestamp: new Date().toISOString(),
          event: {
            type: "select_quest",
            payload: buildPayload({ questId }),
          },
        },
      });
    },
    [sessionState, submittingQuest, sendArcGenAction, buildPayload]
  );

  const handleRegenerateQuests = useCallback(
    (feedback: string) => {
      if (!sessionState || isStreaming) return;
      const eventId = crypto.randomUUID();
      sendArcGenAction("arc_generation_dispatch", {
        sessionId: sessionState.sessionId,
        event: {
          eventId,
          sessionId: sessionState.sessionId,
          clientTimestamp: new Date().toISOString(),
          event: {
            type: "request_more_quests",
            payload: buildPayload({ feedback }),
          },
        },
      });
    },
    [sessionState, isStreaming, sendArcGenAction, buildPayload]
  );

  // Redirect to coaching home after arc activation
  useEffect(() => {
    if (sessionState?.status === "completed" && sessionState?.activatedArcId) {
      const timer = setTimeout(() => navigate("/coaching"), 1000);
      return () => clearTimeout(timer);
    }
  }, [sessionState?.status, sessionState?.activatedArcId, navigate]);

  if (error) {
    return (
      <div className={errorStyle} data-testid="arc-gen-error">
        Error: {error.message} ({error.code})
      </div>
    );
  }

  if (!sessionState || !activeScope) {
    return <div className={loadingStyle}>Setting up your learning journey...</div>;
  }

  // Resonance Path is a different shape end-to-end (palette → texture →
  // draft → tune → anchor → tiny rep → journey).  Once the first
  // snapshot tells us the active recipe is the resonance flow, hand off
  // to its dedicated orchestrator (which reads the same store + WS hook
  // this component already wired up via the mount effect above).
  if (sessionState.recipeMeta?.flow === "resonance") {
    return <ResonanceArcSession />;
  }

  const selectedDomainName = selectedDomainId
    ? domainsById[selectedDomainId]?.name ?? selectedDomainId
    : null;

  const moduleLabel = sessionState.recipeMeta?.moduleLabel;
  const headerLabel = (() => {
    switch (sessionState.step) {
      case "psi": return "Let's find your direction";
      case "macro_hook":
        return moduleLabel ? `${moduleLabel}: a first taste` : "A first taste";
      case "domain_recommend": return "Choose your learning domain";
      case "theta_quiz":
        return moduleLabel ? `${moduleLabel} calibration` : "Quick calibration";
      case "quest_recommend":
        return selectedDomainName
          ? `Your first Quest inside ${selectedDomainName}`
          : "Your first Quest";
      case "quest_confirm": return "You're all set";
      default: return "";
    }
  })();

  return (
    <div className={pageStyle} data-testid="arc-generation-page">
      <div className={headerRowStyle}>
        <div className={titleAreaStyle}>
          <button
            className={backButtonStyle}
            onClick={handleNavigateBack}
            disabled={!canGoBack}
            aria-label="Go back to the previous question"
            title="Go back to the previous question"
            data-testid="arc-gen-back-button"
          >
            ←
          </button>
          <div className={headerStyle}>{headerLabel}</div>
        </div>
      </div>
      {/* Onboarding progress strip — sits ABOVE the per-question
          progress bars so the page reads as: phase context (where
          am I in the whole flow?) → step context (where in this
          phase?) → content.  Recipe-aware: legacy recipes that ask
          the learner to commit to a domain get the extra
          "Select Learning Area" node; Open Path skips it. */}
      {(sessionState.step === "psi" ||
        sessionState.step === "theta_quiz" ||
        sessionState.step === "quest_recommend" ||
        sessionState.step === "quest_confirm") && (
        <OnboardingProgress
          step={sessionState.step}
          hasDomainSelectionStep={
            sessionState.recipeMeta?.hasDomainSelectionStep ?? true
          }
          calibrationOnly={sessionState.recipeMeta?.calibrationOnly ?? false}
          moduleLabel={sessionState.recipeMeta?.moduleLabel}
        />
      )}
      {showCounter && (
        <div
          className={progressRowStyle}
          role="progressbar"
          aria-valuenow={Math.min(currentStepQuestions.count, currentStepQuestions.total)}
          aria-valuemin={0}
          aria-valuemax={currentStepQuestions.total}
          aria-label={`Step progress: question ${Math.min(currentStepQuestions.count, currentStepQuestions.total)} of ${currentStepQuestions.total}`}
          data-testid="arc-gen-question-progress"
        >
          <div className={progressBarsStyle}>
            {Array.from({ length: currentStepQuestions.total }, (_, i) => {
              // Bar i is "done" if it's strictly before the current
              // question, "active" if it IS the current one, and
              // "todo" otherwise.  We use ``answered`` to find the
              // boundary so the active bar reads distinctly from
              // the completed ones.
              const isDone = i < currentStepQuestions.answered;
              const isActive =
                !isDone &&
                currentStepQuestions.active > 0 &&
                i === currentStepQuestions.answered;
              const cls = [
                progressBarStyle,
                isDone ? progressBarDoneStyle : "",
                isActive ? progressBarActiveStyle : "",
              ]
                .filter(Boolean)
                .join(" ");
              const status = isDone ? "done" : isActive ? "active" : "todo";
              return (
                <div
                  key={i}
                  className={cls}
                  data-status={status}
                  data-testid={`arc-gen-progress-bar-${i}`}
                />
              );
            })}
          </div>
          <div className={progressLabelStyle} data-testid="arc-gen-question-counter">
            Question {Math.min(currentStepQuestions.count, currentStepQuestions.total)} of {currentStepQuestions.total}
          </div>
        </div>
      )}

      {exploreQuestsOpen && (
        <ExploreQuests
          quests={allQuests}
          categories={latestCategories}
          milestones={latestMilestones}
          domainName={selectedDomainName ?? ""}
          isStreaming={isStreaming}
          onClose={() => setExploreQuestsOpen(false)}
          onChooseQuest={(qid) => {
            setExploreQuestsOpen(false);
            handleChooseQuest(qid);
          }}
        />
      )}

      <div className={turnsAreaStyle} data-testid="arc-gen-turns">
        {/* Each interaction is a fresh screen — no accumulated history.
            Only the active interaction's coach content is visible. */}
        {/* Coach turn (preamble + question for free_text /
            single_select / multi_select).  Hidden during the
            calibration-feedback phase so the only thing on screen
            is the feedback bubble + Continue button. */}
        {activeInteraction &&
          activeInteraction.kind !== "domain_recommendation" &&
          activeInteraction.kind !== "quest_recommendation" &&
          !showCalibrationFeedback && (
            <CoachInteractionView interaction={activeInteraction} />
          )}

        {/* Domain recommendation intro bubble */}
        {activeInteraction?.kind === "domain_recommendation" &&
          (activeInteraction as ArcDomainRecommendationSpec).intro && (
            <div className={turnStyle}>
              <div className={coachBubbleStyle}>
                <MathMarkdown>
                  {(activeInteraction as ArcDomainRecommendationSpec).intro!}
                </MathMarkdown>
              </div>
            </div>
          )}

        {/* Quest recommendation intro — uses the wider, scannable
            quest-intro bubble.  Markdown paragraph + list spacing is
            handled by the bubble's CSS so a multi-paragraph intro
            doesn't collapse into a wall of text. */}
        {activeInteraction?.kind === "quest_recommendation" &&
          (activeInteraction as ArcQuestRecommendationSpec).intro && (
            <div
              className={questIntroBubbleStyle}
              data-testid="quest-recommendation-intro"
            >
              <MathMarkdown>
                {(activeInteraction as ArcQuestRecommendationSpec).intro!}
              </MathMarkdown>
            </div>
          )}

        {/* Loading indicators — recommendation lists hold off rendering
            until streaming completes so they don't render with partial
            tier / shape values. */}
        {sessionState.step === "domain_recommend" && isStreaming && (
          <div className={turnStyle}>
            <PendingIndicator
              variant="block"
              label="Mapping your learning landscape…"
              data-testid="arc-gen-domain-loading"
            />
          </div>
        )}
        {/* Initial quest-recommendation wait — only until the first
            quest streams in.  Once cards can render, the
            QuestRecommendation block below takes over and shows a
            smaller "still generating the map" indicator instead. */}
        {sessionState.step === "quest_recommend" && isStreaming &&
          allQuests.length === 0 && (
            <div className={turnStyle}>
              <PendingIndicator
                variant="block"
                label="Shaping your Quest recommendations…"
                data-testid="arc-gen-quest-loading"
              />
            </div>
          )}

        {/* Domain strip */}
        {latestRecommendations.length > 0 && !isStreaming &&
          sessionState.step === "domain_recommend" && (
            <DomainStrip
              recommendations={latestRecommendations}
              selectedDomainId={selectedDomainId}
              isStreaming={isStreaming}
              submitting={submittingDomain}
              onSelect={handleSelectDomain}
              onConfirm={handleConfirmDomain}
            />
          )}

        {/* Quest recommendation — renders progressively: the hero
            card and alternatives appear as soon as they stream in,
            while the rest of the map keeps generating.  No longer
            gated on ``!isStreaming``. */}
        {allQuests.length > 0 &&
          (sessionState.step === "quest_recommend" || sessionState.step === "quest_confirm") && (
            <>
              <QuestRecommendation
                heroQuest={latestHeroQuest}
                quests={latestQuests}
                categories={latestCategories}
                milestones={latestMilestones}
                isStreaming={isStreaming}
                heroReady={heroReady}
                submittingQuest={submittingQuest}
                onChooseQuest={handleChooseQuest}
                onExploreQuest={() => setExploreQuestsOpen(true)}
                onExploreAll={() => setExploreQuestsOpen(true)}
              />
              {sessionState.step === "quest_recommend" && !isStreaming && (
                <QuestEscapeHatch
                  isStreaming={isStreaming}
                  onRegenerate={handleRegenerateQuests}
                />
              )}
            </>
          )}
      </div>

      {/* Input area */}
      <div className={inputAreaStyle}>
        {submissionPending &&
          (sessionState.step === "psi" || sessionState.step === "theta_quiz") && (
            <PendingIndicator
              variant="block"
              label="Sending your answer…"
              data-testid="arc-gen-pending"
            />
          )}

        {activeInteraction?.kind === "free_text" && !submissionPending && (
          <>
            <textarea
              className={textareaStyle}
              placeholder="Type your answer..."
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (textInput.trim()) handleSubmitFreeText();
                }
              }}
              data-testid="arc-gen-text-input"
            />
            {sessionState?.step === "psi" &&
              currentStepQuestions.count === 1 &&
              !textInput.trim() && (
                <div
                  className={sparkChipsWrapperStyle}
                  data-testid="arc-gen-q1-spark-chips"
                >
                  <div className={sparkChipsLabelStyle}>
                    Or start from one of these
                  </div>
                  <div className={sparkChipsRowStyle}>
                    {Q1_SPARK_CHIPS.map((chip) => (
                      <button
                        key={chip}
                        type="button"
                        className={sparkChipStyle}
                        onClick={() => setTextInput(chip)}
                        data-testid="arc-gen-q1-spark-chip"
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            <button
              className={submitBtnStyle}
              onClick={handleSubmitFreeText}
              disabled={!textInput.trim()}
              data-testid="arc-gen-submit-button"
            >
              Send
            </button>
          </>
        )}

        {activeInteraction?.kind === "single_select" &&
          !submissionPending &&
          (activeInteraction as any).prompt?.options && (() => {
            const ss = activeInteraction as any;
            const interactionId = ss.interactionId;
            // Pydantic schema renamed ``explanation`` → ``preamble``;
            // older sessions persisted the legacy field name, so
            // accept either.
            const preamble: string | undefined =
              ss.preamble ?? ss.explanation;
            // Calibration feedback phase: show the LLM's
            // post-answer feedback (carried in the new question's
            // preamble) BEFORE revealing the next question's
            // options.  Mirrors the coaching-session pattern.
            const showCalibrationFeedback =
              hasPriorAnsweredTheta &&
              !thetaFeedbackDismissed[interactionId];
            if (showCalibrationFeedback) {
              return (
                <div data-testid="arc-gen-calibration-feedback">
                  {preamble ? (
                    <div
                      className={calibrationFeedbackStyle}
                      data-testid="calibration-feedback-bubble"
                    >
                      <MathMarkdown>{preamble}</MathMarkdown>
                    </div>
                  ) : (
                    <PendingIndicator
                      variant="block"
                      label="Reviewing your answer…"
                      data-testid="calibration-feedback-loading"
                    />
                  )}
                  {!isStreaming && (
                    <button
                      className={calibrationContinueBtnStyle}
                      onClick={() =>
                        setThetaFeedbackDismissed((prev) => ({
                          ...prev,
                          [interactionId]: true,
                        }))
                      }
                      data-testid="calibration-continue"
                    >
                      Continue to next question
                    </button>
                  )}
                </div>
              );
            }
            // Question phase — preamble (if any) is rendered above
            // by CoachInteractionView; this section just renders
            // the option buttons + Other escape hatch.
            return (
              <div data-testid="arc-gen-single-select">
                {ss.prompt.options.map((opt: any) => (
                  <button
                    key={opt.id}
                    className={optionBtnStyle}
                    onClick={() => {
                      setOtherOpen(false);
                      handleSelectOption(opt.id, opt.label);
                    }}
                    data-testid={`arc-gen-option-${opt.id}`}
                  >
                    <MathText text={opt.label} />
                  </button>
                ))}
                {ss.allowOther && !otherOpen && (
                  <button
                    className={optionBtnStyle}
                    onClick={() => setOtherOpen(true)}
                    data-testid="arc-gen-option-other-toggle"
                  >
                    Other — I'll type my own
                  </button>
                )}
                {ss.allowOther && otherOpen && (
                  <div className={otherInputAreaStyle}>
                    <textarea
                      className={otherTextareaStyle}
                      value={otherText}
                      onChange={(e) => setOtherText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          if (!activeInteraction || !otherText.trim()) return;
                          handleSelectOption("__other__", otherText.trim());
                          setOtherText("");
                          setOtherOpen(false);
                        }
                      }}
                      placeholder="Type your answer…"
                      rows={2}
                      autoFocus
                    />
                    <button
                      className={submitBtnStyle}
                      disabled={!otherText.trim()}
                      onClick={() => {
                        if (!activeInteraction || !otherText.trim()) return;
                        handleSelectOption("__other__", otherText.trim());
                        setOtherText("");
                        setOtherOpen(false);
                      }}
                      data-testid="arc-gen-option-other-submit"
                    >
                      Send
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

        {activeInteraction?.kind === "multi_select" &&
          !submissionPending &&
          (activeInteraction as ArcMultiSelectSpec).prompt?.options && (() => {
            const ms = activeInteraction as ArcMultiSelectSpec;
            const interactionId = ms.interactionId;
            const opts = ms.prompt!.options;
            const minSelect = ms.minSelect ?? 1;
            const maxSelect = ms.maxSelect ?? opts.length;
            const picks = multiSelectPicks[interactionId] ?? [];
            const togglePick = (id: string) => {
              setMultiSelectPicks((prev) => {
                const current = prev[interactionId] ?? [];
                if (current.includes(id)) {
                  return { ...prev, [interactionId]: current.filter((x) => x !== id) };
                }
                if (current.length >= maxSelect) {
                  // At max — drop the oldest pick to make room.
                  return { ...prev, [interactionId]: [...current.slice(1), id] };
                }
                return { ...prev, [interactionId]: [...current, id] };
              });
            };
            const canSubmit = picks.length >= minSelect && picks.length <= maxSelect;
            // Phrase the hint label so it nudges *prioritization*
            // when the bounds force a fixed pick count (e.g.
            // "Pick top 2" rather than "Pick 2") — the "top" word
            // matters for forced-rank questions like Q5 of the
            // Open Path PSI battery.
            const hint =
              minSelect === maxSelect
                ? minSelect === 1
                  ? "Pick one"
                  : `Pick top ${minSelect}`
                : minSelect > 1
                  ? `Pick at least ${minSelect}`
                  : "Pick all that apply";
            return (
              <div data-testid="arc-gen-multi-select">
                <div className={multiSelectHintStyle}>{hint}</div>
                {opts.map((opt) => {
                  const selected = picks.includes(opt.id);
                  const cls = selected
                    ? `${optionBtnStyle} ${multiOptionSelectedStyle}`
                    : optionBtnStyle;
                  return (
                    <button
                      key={opt.id}
                      className={cls}
                      onClick={() => togglePick(opt.id)}
                      data-testid={`arc-gen-option-${opt.id}`}
                      aria-pressed={selected}
                    >
                      <span className={checkboxStyle}>
                        {selected ? "☑" : "☐"}
                      </span>
                      <MathText text={opt.label} />
                    </button>
                  );
                })}
                <button
                  className={submitBtnStyle}
                  onClick={handleSubmitMultiSelect}
                  disabled={!canSubmit}
                  data-testid="arc-gen-multi-select-submit"
                >
                  {canSubmit ? "Continue" : `Select ${minSelect - picks.length} more`}
                </button>
              </div>
            );
          })()}

        {/* Domain recommendation: sticky CTA + loading indicator */}
        {sessionState.step === "domain_recommend" && !isStreaming && selectedDomainId && (
          <>
            <button
              className={submitBtnStyle}
              onClick={() => handleConfirmDomain()}
              disabled={submittingDomain}
              data-testid="arc-gen-confirm-domain"
            >
              {submittingDomain
                ? "Setting up your placement…"
                : `Continue with ${selectedDomainName}`}
            </button>
            {submittingDomain && (
              <div style={{ marginTop: 8 }}>
                <PendingIndicator
                  variant="block"
                  label="Preparing your placement…"
                  data-testid="arc-gen-domain-submitting"
                />
              </div>
            )}
          </>
        )}

        {sessionState.status === "completed" && (
          <div className={completedStyle} data-testid="arc-gen-completed">
            Your Quest is activated! Redirecting...
          </div>
        )}
      </div>
    </div>
  );

  function CoachInteractionView({ interaction }: { interaction: ArcInteractionSpec }) {
    const spec = interaction as any;
    // Pydantic schema renamed ``explanation`` → ``preamble``; older
    // sessions persisted the legacy field name, so accept either.
    const preamble: string | undefined = spec.preamble ?? spec.explanation;
    // For theta_quiz single_select questions where feedback was
    // already shown (and dismissed), we don't render the preamble
    // again above the question — the preamble was the feedback.
    // For all other contexts, show the preamble normally above the
    // question.
    const isThetaPostFeedback =
      spec.kind === "single_select" &&
      spec.step === "theta_quiz" &&
      hasPriorAnsweredTheta;
    const showPreamble = !!preamble && !isThetaPostFeedback;
    return (
      <div className={turnStyle}>
        {spec.kind === "free_text" && showPreamble && (
          <div className={coachBubbleStyle}>
            <MathMarkdown>{preamble}</MathMarkdown>
          </div>
        )}
        {spec.kind === "free_text" && spec.prompt?.question && (
          <div className={coachBubbleStyle}>
            <MathMarkdown>{spec.prompt.question}</MathMarkdown>
          </div>
        )}
        {spec.kind === "single_select" && showPreamble && (
          <div className={coachBubbleStyle} data-testid="single-select-preamble">
            <MathMarkdown>{preamble}</MathMarkdown>
          </div>
        )}
        {spec.kind === "single_select" && spec.question && (
          <div className={coachBubbleStyle}>
            <MathMarkdown>{spec.question}</MathMarkdown>
          </div>
        )}
        {spec.kind === "multi_select" && showPreamble && (
          <div className={coachBubbleStyle} data-testid="multi-select-preamble">
            <MathMarkdown>{preamble}</MathMarkdown>
          </div>
        )}
        {spec.kind === "multi_select" && spec.question && (
          <div className={coachBubbleStyle}>
            <MathMarkdown>{spec.question}</MathMarkdown>
          </div>
        )}
      </div>
    );
  }
};
